import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  postPizzasCount,
  usePostGetAllPizzas,
} from "@/api/pizza-checker/pizzas/pizzas";
import { PizzaDTO, PizzasDTO } from "@/api/custom-schema";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { usePermissions } from "@/hooks/use-permissions";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import { PHOTO_QUALITY_CHECK_CONDITIONS } from "@/pizzas/photo-quality-checks";
import { componentErrorCondition } from "@/pizzas/component-errors";
import {
  getStoredBoolean,
  getStoredDate,
  getStoredNumber,
  getStoredString,
  getStoredStringArray,
  isStoredStateRecord,
  readLocalStorageState,
  StoredStateRecord,
  writeLocalStorageState,
} from "@/hooks/use-local-storage-state";

interface PizzaContextType {
  pizzas: PizzaDTO[] | undefined;
  /** the page is being (re)loaded: `pizzas` can be a cached copy from before new captures shifted the list */
  pizzasFetching: boolean;
  /** when `pizzas` was loaded (ms epoch, 0 = not yet) */
  pizzasUpdatedAt: number;
  refetchPizzas: () => void;
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  setPerPage: (perPage: number) => void;
  totalPizzas: number | undefined;
  /** NOK pizzas among the filtered ones (the list header's summary) */
  nokTotal: number | undefined;
  totalPages: number | undefined;
  idFilter: string;
  setIdFilter: (ids: string) => void;
  orderFilter: string;
  setOrderFilter: (ids: string) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortOrder: "asc" | "desc";
  setSortOrder: (sortOrder: "asc" | "desc") => void;
  captureAtFilterStart: Date | undefined;
  setCaptureAtFilterStart: (date: Date | undefined) => void;
  captureAtFilterEnd: Date | undefined;
  setCaptureAtFilterEnd: (date: Date | undefined) => void;
  classificationFilter: string;
  setClassificationFilter: (classification: string) => void;
  nameFilter: string;
  setNameFilter: (name: string) => void;
  confidenceFilter: string;
  setConfidenceFilter: (confidence: string) => void;
  captureCountFilter: string;
  setCaptureCountFilter: (count: string) => void;
  componentCountFilter: string;
  setComponentCountFilter: (count: string) => void;
  neuralClassificationFilter: string;
  setNeuralClassificationFilter: (classification: string) => void;
  traditionalClassificationFilter: string;
  setTraditionalClassificationFilter: (classification: string) => void;
  donenessFilter: string;
  setDonenessFilter: (doneness: string) => void;
  overallEvaluationFilter: string;
  setOverallEvaluationFilter: (status: string) => void;
  componentsEvaluationFilter: string;
  setComponentsEvaluationFilter: (status: string) => void;
  pizzaSaverEvaluationFilter: string;
  setPizzaSaverEvaluationFilter: (status: string) => void;
  pizzaTypeFilter: string[];
  setPizzaTypeFilter: (types: string[]) => void;
  overallEvaluationStatusFilter: string[];
  setOverallEvaluationStatusFilter: (statuses: string[]) => void;
  baseComponentsStatusFilter: string[];
  setBaseComponentsStatusFilter: (statuses: string[]) => void;
  extrasStatusFilter: string[];
  setExtrasStatusFilter: (statuses: string[]) => void;
  removalsStatusFilter: string[];
  setRemovalsStatusFilter: (statuses: string[]) => void;
  pizzaSaverStatusFilter: string[];
  setPizzaSaverStatusFilter: (statuses: string[]) => void;
  hasExtrasFilter: boolean;
  setHasExtrasFilter: (value: boolean) => void;
  hasRemovalsFilter: boolean;
  setHasRemovalsFilter: (value: boolean) => void;
  photoQualityStatusFilter: string[];
  setPhotoQualityStatusFilter: (statuses: string[]) => void;
  photoQualityCheckFilter: string[];
  setPhotoQualityCheckFilter: (checks: string[]) => void;
  componentErrorFilter: string;
  setComponentErrorFilter: (components: string) => void;
  /** Clears every filter (paging size and sorting are kept). */
  resetFilters: () => void;
  /**
   * Position of a pizza in the filtered, sorted list: the number of matching
   * pizzas sorted before it (a pizza outside the filter gets the position it
   * would have). null when the sort value cannot be compared. `fresh` = never
   * answered from the backend's count cache.
   */
  locatePizza: (pizza: object, options?: { fresh?: boolean }) => Promise<number | null>;
}

interface PizzaProviderProps {
  children: React.ReactNode;
}

const PizzaContext = createContext<PizzaContextType | undefined>(undefined);
const PIZZA_FILTER_STORAGE_KEY = "pizza-admin:pizza-filters:v1";

type PizzaFilterState = {
  perPage: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  idFilter: string;
  orderFilter: string;
  captureAtFilterStart: Date | undefined;
  captureAtFilterEnd: Date | undefined;
  classificationFilter: string;
  nameFilter: string;
  confidenceFilter: string;
  captureCountFilter: string;
  componentCountFilter: string;
  neuralClassificationFilter: string;
  traditionalClassificationFilter: string;
  donenessFilter: string;
  overallEvaluationFilter: string;
  componentsEvaluationFilter: string;
  pizzaSaverEvaluationFilter: string;
  pizzaTypeFilter: string[];
  overallEvaluationStatusFilter: string[];
  baseComponentsStatusFilter: string[];
  extrasStatusFilter: string[];
  removalsStatusFilter: string[];
  pizzaSaverStatusFilter: string[];
  hasExtrasFilter: boolean;
  hasRemovalsFilter: boolean;
  photoQualityStatusFilter: string[];
  photoQualityCheckFilter: string[];
  componentErrorFilter: string;
};

const DEFAULT_PIZZA_FILTER_STATE: PizzaFilterState = {
  perPage: 10,
  sortBy: "capture_at",
  sortOrder: "desc",
  idFilter: "",
  orderFilter: "",
  captureAtFilterStart: undefined,
  captureAtFilterEnd: undefined,
  classificationFilter: "",
  nameFilter: "",
  confidenceFilter: "",
  captureCountFilter: "",
  componentCountFilter: "",
  neuralClassificationFilter: "",
  traditionalClassificationFilter: "",
  donenessFilter: "",
  overallEvaluationFilter: "",
  componentsEvaluationFilter: "",
  pizzaSaverEvaluationFilter: "",
  pizzaTypeFilter: [],
  overallEvaluationStatusFilter: [],
  baseComponentsStatusFilter: [],
  extrasStatusFilter: [],
  removalsStatusFilter: [],
  pizzaSaverStatusFilter: [],
  hasExtrasFilter: false,
  hasRemovalsFilter: false,
  photoQualityStatusFilter: [],
  photoQualityCheckFilter: [],
  componentErrorFilter: "",
};

const getValueByPath = (source: object, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (value, key) =>
        value != null && typeof value === "object"
          ? (value as Record<string, unknown>)[key]
          : undefined,
      source,
    );

const getStoredSortOrder = (
  record: StoredStateRecord,
  key: string,
  fallback: "asc" | "desc",
) => {
  const value = record[key];
  return value === "asc" || value === "desc" ? value : fallback;
};

const readPizzaFilterState = (): PizzaFilterState => {
  const storedState = readLocalStorageState<StoredStateRecord>(
    PIZZA_FILTER_STORAGE_KEY,
    {},
    (storedValue) => (isStoredStateRecord(storedValue) ? storedValue : {}),
  );
  const perPage = getStoredNumber(
    storedState,
    "perPage",
    DEFAULT_PIZZA_FILTER_STATE.perPage,
  );

  return {
    perPage: perPage > 0 ? perPage : DEFAULT_PIZZA_FILTER_STATE.perPage,
    sortBy: getStoredString(
      storedState,
      "sortBy",
      DEFAULT_PIZZA_FILTER_STATE.sortBy,
    ),
    sortOrder: getStoredSortOrder(
      storedState,
      "sortOrder",
      DEFAULT_PIZZA_FILTER_STATE.sortOrder,
    ),
    idFilter: getStoredString(
      storedState,
      "idFilter",
      DEFAULT_PIZZA_FILTER_STATE.idFilter,
    ),
    orderFilter: getStoredString(
      storedState,
      "orderFilter",
      DEFAULT_PIZZA_FILTER_STATE.orderFilter,
    ),
    captureAtFilterStart: getStoredDate(
      storedState,
      "captureAtFilterStart",
      DEFAULT_PIZZA_FILTER_STATE.captureAtFilterStart,
    ),
    captureAtFilterEnd: getStoredDate(
      storedState,
      "captureAtFilterEnd",
      DEFAULT_PIZZA_FILTER_STATE.captureAtFilterEnd,
    ),
    classificationFilter: getStoredString(
      storedState,
      "classificationFilter",
      DEFAULT_PIZZA_FILTER_STATE.classificationFilter,
    ),
    nameFilter: getStoredString(
      storedState,
      "nameFilter",
      DEFAULT_PIZZA_FILTER_STATE.nameFilter,
    ),
    confidenceFilter: getStoredString(
      storedState,
      "confidenceFilter",
      DEFAULT_PIZZA_FILTER_STATE.confidenceFilter,
    ),
    captureCountFilter: getStoredString(
      storedState,
      "captureCountFilter",
      DEFAULT_PIZZA_FILTER_STATE.captureCountFilter,
    ),
    componentCountFilter: getStoredString(
      storedState,
      "componentCountFilter",
      DEFAULT_PIZZA_FILTER_STATE.componentCountFilter,
    ),
    neuralClassificationFilter: getStoredString(
      storedState,
      "neuralClassificationFilter",
      DEFAULT_PIZZA_FILTER_STATE.neuralClassificationFilter,
    ),
    traditionalClassificationFilter: getStoredString(
      storedState,
      "traditionalClassificationFilter",
      DEFAULT_PIZZA_FILTER_STATE.traditionalClassificationFilter,
    ),
    donenessFilter: getStoredString(
      storedState,
      "donenessFilter",
      DEFAULT_PIZZA_FILTER_STATE.donenessFilter,
    ),
    overallEvaluationFilter: getStoredString(
      storedState,
      "overallEvaluationFilter",
      DEFAULT_PIZZA_FILTER_STATE.overallEvaluationFilter,
    ),
    componentsEvaluationFilter: getStoredString(
      storedState,
      "componentsEvaluationFilter",
      DEFAULT_PIZZA_FILTER_STATE.componentsEvaluationFilter,
    ),
    pizzaSaverEvaluationFilter: getStoredString(
      storedState,
      "pizzaSaverEvaluationFilter",
      DEFAULT_PIZZA_FILTER_STATE.pizzaSaverEvaluationFilter,
    ),
    pizzaTypeFilter: getStoredStringArray(
      storedState,
      "pizzaTypeFilter",
      DEFAULT_PIZZA_FILTER_STATE.pizzaTypeFilter,
    ),
    overallEvaluationStatusFilter: getStoredStringArray(
      storedState,
      "overallEvaluationStatusFilter",
      DEFAULT_PIZZA_FILTER_STATE.overallEvaluationStatusFilter,
    ),
    baseComponentsStatusFilter: getStoredStringArray(
      storedState,
      "baseComponentsStatusFilter",
      DEFAULT_PIZZA_FILTER_STATE.baseComponentsStatusFilter,
    ),
    extrasStatusFilter: getStoredStringArray(
      storedState,
      "extrasStatusFilter",
      DEFAULT_PIZZA_FILTER_STATE.extrasStatusFilter,
    ),
    removalsStatusFilter: getStoredStringArray(
      storedState,
      "removalsStatusFilter",
      DEFAULT_PIZZA_FILTER_STATE.removalsStatusFilter,
    ),
    pizzaSaverStatusFilter: getStoredStringArray(
      storedState,
      "pizzaSaverStatusFilter",
      DEFAULT_PIZZA_FILTER_STATE.pizzaSaverStatusFilter,
    ),
    hasExtrasFilter: getStoredBoolean(
      storedState,
      "hasExtrasFilter",
      DEFAULT_PIZZA_FILTER_STATE.hasExtrasFilter,
    ),
    hasRemovalsFilter: getStoredBoolean(
      storedState,
      "hasRemovalsFilter",
      DEFAULT_PIZZA_FILTER_STATE.hasRemovalsFilter,
    ),
    photoQualityStatusFilter: getStoredStringArray(
      storedState,
      "photoQualityStatusFilter",
      DEFAULT_PIZZA_FILTER_STATE.photoQualityStatusFilter,
    ),
    photoQualityCheckFilter: getStoredStringArray(
      storedState,
      "photoQualityCheckFilter",
      DEFAULT_PIZZA_FILTER_STATE.photoQualityCheckFilter,
    ).filter((value) => value in PHOTO_QUALITY_CHECK_CONDITIONS),
    componentErrorFilter: getStoredString(
      storedState,
      "componentErrorFilter",
      DEFAULT_PIZZA_FILTER_STATE.componentErrorFilter,
    ),
  };
};

export const PizzaProvider = ({ children }: PizzaProviderProps) => {
  const { hasPermission } = usePermissions();
  const branchFilter = useBranchFilter();
  const initialFilterState = useMemo(readPizzaFilterState, []);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(initialFilterState.perPage);
  const [idFilter, setIdFilter] = useState(initialFilterState.idFilter);
  const [orderFilter, setOrderFilter] = useState(
    initialFilterState.orderFilter,
  );
  const [sortBy, setSortBy] = useState(initialFilterState.sortBy);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    initialFilterState.sortOrder,
  );
  const [captureAtFilterStart, setCaptureAtFilterStart] = useState<
    Date | undefined
  >(initialFilterState.captureAtFilterStart);
  const [captureAtFilterEnd, setCaptureAtFilterEnd] = useState<
    Date | undefined
  >(initialFilterState.captureAtFilterEnd);
  const [classificationFilter, setClassificationFilter] = useState<string>(
    initialFilterState.classificationFilter,
  );
  const [nameFilter, setNameFilter] = useState<string>(
    initialFilterState.nameFilter,
  );
  const [confidenceFilter, setConfidenceFilter] = useState<string>(
    initialFilterState.confidenceFilter,
  );
  const [captureCountFilter, setCaptureCountFilter] = useState<string>(
    initialFilterState.captureCountFilter,
  );
  const [componentCountFilter, setComponentCountFilter] = useState<string>(
    initialFilterState.componentCountFilter,
  );
  const [neuralClassificationFilter, setNeuralClassificationFilter] =
    useState<string>(initialFilterState.neuralClassificationFilter);
  const [traditionalClassificationFilter, setTraditionalClassificationFilter] =
    useState<string>(initialFilterState.traditionalClassificationFilter);
  const [donenessFilter, setDonenessFilter] = useState<string>(
    initialFilterState.donenessFilter,
  );
  const [overallEvaluationFilter, setOverallEvaluationFilter] =
    useState<string>(initialFilterState.overallEvaluationFilter);
  const [componentsEvaluationFilter, setComponentsEvaluationFilter] =
    useState<string>(initialFilterState.componentsEvaluationFilter);
  const [pizzaSaverEvaluationFilter, setPizzaSaverEvaluationFilter] =
    useState<string>(initialFilterState.pizzaSaverEvaluationFilter);
  const [pizzaTypeFilter, setPizzaTypeFilter] = useState<string[]>(
    initialFilterState.pizzaTypeFilter,
  );
  const [overallEvaluationStatusFilter, setOverallEvaluationStatusFilter] =
    useState<string[]>(initialFilterState.overallEvaluationStatusFilter);
  const [baseComponentsStatusFilter, setBaseComponentsStatusFilter] = useState<
    string[]
  >(initialFilterState.baseComponentsStatusFilter);
  const [extrasStatusFilter, setExtrasStatusFilter] = useState<string[]>(
    initialFilterState.extrasStatusFilter,
  );
  const [removalsStatusFilter, setRemovalsStatusFilter] = useState<string[]>(
    initialFilterState.removalsStatusFilter,
  );
  const [pizzaSaverStatusFilter, setPizzaSaverStatusFilter] = useState<
    string[]
  >(initialFilterState.pizzaSaverStatusFilter);
  const [hasExtrasFilter, setHasExtrasFilter] = useState<boolean>(
    initialFilterState.hasExtrasFilter,
  );
  const [hasRemovalsFilter, setHasRemovalsFilter] = useState<boolean>(
    initialFilterState.hasRemovalsFilter,
  );
  const [photoQualityStatusFilter, setPhotoQualityStatusFilter] = useState<
    string[]
  >(initialFilterState.photoQualityStatusFilter);
  const [photoQualityCheckFilter, setPhotoQualityCheckFilter] = useState<
    string[]
  >(initialFilterState.photoQualityCheckFilter);
  const [componentErrorFilter, setComponentErrorFilter] = useState<string>(
    initialFilterState.componentErrorFilter,
  );

  const resetFilters = useCallback(() => {
    const defaults = DEFAULT_PIZZA_FILTER_STATE;
    setIdFilter(defaults.idFilter);
    setOrderFilter(defaults.orderFilter);
    setCaptureAtFilterStart(defaults.captureAtFilterStart);
    setCaptureAtFilterEnd(defaults.captureAtFilterEnd);
    setClassificationFilter(defaults.classificationFilter);
    setNameFilter(defaults.nameFilter);
    setConfidenceFilter(defaults.confidenceFilter);
    setCaptureCountFilter(defaults.captureCountFilter);
    setComponentCountFilter(defaults.componentCountFilter);
    setNeuralClassificationFilter(defaults.neuralClassificationFilter);
    setTraditionalClassificationFilter(
      defaults.traditionalClassificationFilter,
    );
    setDonenessFilter(defaults.donenessFilter);
    setOverallEvaluationFilter(defaults.overallEvaluationFilter);
    setComponentsEvaluationFilter(defaults.componentsEvaluationFilter);
    setPizzaSaverEvaluationFilter(defaults.pizzaSaverEvaluationFilter);
    setPizzaTypeFilter(defaults.pizzaTypeFilter);
    setOverallEvaluationStatusFilter(defaults.overallEvaluationStatusFilter);
    setBaseComponentsStatusFilter(defaults.baseComponentsStatusFilter);
    setExtrasStatusFilter(defaults.extrasStatusFilter);
    setRemovalsStatusFilter(defaults.removalsStatusFilter);
    setPizzaSaverStatusFilter(defaults.pizzaSaverStatusFilter);
    setHasExtrasFilter(defaults.hasExtrasFilter);
    setHasRemovalsFilter(defaults.hasRemovalsFilter);
    setPhotoQualityStatusFilter(defaults.photoQualityStatusFilter);
    setPhotoQualityCheckFilter(defaults.photoQualityCheckFilter);
    setComponentErrorFilter(defaults.componentErrorFilter);
  }, []);

  const currentFilterState = useMemo<PizzaFilterState>(
    () => ({
      perPage,
      sortBy,
      sortOrder,
      idFilter,
      orderFilter,
      captureAtFilterStart,
      captureAtFilterEnd,
      classificationFilter,
      nameFilter,
      confidenceFilter,
      captureCountFilter,
      componentCountFilter,
      neuralClassificationFilter,
      traditionalClassificationFilter,
      donenessFilter,
      overallEvaluationFilter,
      componentsEvaluationFilter,
      pizzaSaverEvaluationFilter,
      pizzaTypeFilter,
      overallEvaluationStatusFilter,
      baseComponentsStatusFilter,
      extrasStatusFilter,
      removalsStatusFilter,
      pizzaSaverStatusFilter,
      hasExtrasFilter,
      hasRemovalsFilter,
      photoQualityStatusFilter,
      photoQualityCheckFilter,
      componentErrorFilter,
    }),
    [
      perPage,
      sortBy,
      sortOrder,
      idFilter,
      orderFilter,
      captureAtFilterStart,
      captureAtFilterEnd,
      classificationFilter,
      nameFilter,
      confidenceFilter,
      captureCountFilter,
      componentCountFilter,
      neuralClassificationFilter,
      traditionalClassificationFilter,
      donenessFilter,
      overallEvaluationFilter,
      componentsEvaluationFilter,
      pizzaSaverEvaluationFilter,
      pizzaTypeFilter,
      overallEvaluationStatusFilter,
      baseComponentsStatusFilter,
      extrasStatusFilter,
      removalsStatusFilter,
      pizzaSaverStatusFilter,
      hasExtrasFilter,
      hasRemovalsFilter,
      photoQualityStatusFilter,
      photoQualityCheckFilter,
      componentErrorFilter,
    ],
  );

  useEffect(() => {
    writeLocalStorageState(PIZZA_FILTER_STORAGE_KEY, {
      ...currentFilterState,
      captureAtFilterStart:
        currentFilterState.captureAtFilterStart?.toISOString(),
      captureAtFilterEnd: currentFilterState.captureAtFilterEnd?.toISOString(),
    });
  }, [currentFilterState]);

  useEffect(() => {
    setPage(0);
  }, [
    idFilter,
    orderFilter,
    captureAtFilterStart,
    captureAtFilterEnd,
    classificationFilter,
    nameFilter,
    confidenceFilter,
    captureCountFilter,
    componentCountFilter,
    neuralClassificationFilter,
    traditionalClassificationFilter,
    donenessFilter,
    overallEvaluationFilter,
    componentsEvaluationFilter,
    pizzaSaverEvaluationFilter,
    pizzaTypeFilter,
    overallEvaluationStatusFilter,
    baseComponentsStatusFilter,
    extrasStatusFilter,
    removalsStatusFilter,
    pizzaSaverStatusFilter,
    hasExtrasFilter,
    hasRemovalsFilter,
    photoQualityStatusFilter,
    photoQualityCheckFilter,
    componentErrorFilter,
  ]);

  // Debounced filter values
  const debouncedIdFilter = useDebounce(idFilter, 400);
  const debouncedOrderFilter = useDebounce(orderFilter, 400);
  const debouncedClassificationFilter = useDebounce(classificationFilter, 400);
  const debouncedNameFilter = useDebounce(nameFilter, 400);
  const debouncedCaptureAtFilterStart = useDebounce(captureAtFilterStart, 400);
  const debouncedCaptureAtFilterEnd = useDebounce(captureAtFilterEnd, 400);
  const debouncedConfidenceFilter = useDebounce(confidenceFilter, 400);
  const debouncedCaptureCountFilter = useDebounce(captureCountFilter, 400);
  const debouncedComponentCountFilter = useDebounce(componentCountFilter, 400);
  const debouncedNeuralClassificationFilter = useDebounce(
    neuralClassificationFilter,
    400,
  );
  const debouncedTraditionalClassificationFilter = useDebounce(
    traditionalClassificationFilter,
    400,
  );
  const debouncedDonenessFilter = useDebounce(donenessFilter, 400);
  const debouncedOverallEvaluationFilter = useDebounce(
    overallEvaluationFilter,
    400,
  );
  const debouncedComponentsEvaluationFilter = useDebounce(
    componentsEvaluationFilter,
    400,
  );
  const debouncedPizzaSaverEvaluationFilter = useDebounce(
    pizzaSaverEvaluationFilter,
    400,
  );
  const debouncedPizzaTypeFilter = useDebounce(pizzaTypeFilter, 400);
  const debouncedOverallEvaluationStatusFilter = useDebounce(
    overallEvaluationStatusFilter,
    400,
  );
  const debouncedBaseComponentsStatusFilter = useDebounce(
    baseComponentsStatusFilter,
    400,
  );
  const debouncedExtrasStatusFilter = useDebounce(extrasStatusFilter, 400);
  const debouncedRemovalsStatusFilter = useDebounce(removalsStatusFilter, 400);
  const debouncedPizzaSaverStatusFilter = useDebounce(
    pizzaSaverStatusFilter,
    400,
  );
  const debouncedHasExtrasFilter = useDebounce(hasExtrasFilter, 400);
  const debouncedHasRemovalsFilter = useDebounce(hasRemovalsFilter, 400);
  const debouncedPhotoQualityStatusFilter = useDebounce(
    photoQualityStatusFilter,
    400,
  );
  const debouncedPhotoQualityCheckFilter = useDebounce(
    photoQualityCheckFilter,
    400,
  );
  const debouncedComponentErrorFilter = useDebounce(componentErrorFilter, 400);

  const filters = useMemo(() => {
    const captureAtFilter =
      debouncedCaptureAtFilterStart || debouncedCaptureAtFilterEnd
        ? {
            ...(debouncedCaptureAtFilterStart
              ? {
                  $gte: debouncedCaptureAtFilterStart.toISOString(),
                }
              : {}),
            ...(debouncedCaptureAtFilterEnd
              ? {
                  $lte: debouncedCaptureAtFilterEnd.toISOString(),
                }
              : {}),
          }
        : undefined;

    const nameFilterOr = debouncedNameFilter
      ? [
          ...debouncedNameFilter.split(",").map((value) => ({
            name: { $regex: value.trim(), $options: "i" },
          })),
        ]
      : [{}];

    const componentErrorConditions = debouncedComponentErrorFilter
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .map((component) => componentErrorCondition(component));

    return {
      ...branchFilter,
      $and: [
        {
          evaluation: { $exists: true },
        },
        {
          order_id: { $exists: true },
        },
        {
          $or: [
            ...(debouncedIdFilter
              ? debouncedIdFilter.split(",").map((value) => ({
                  id: { $regex: value.trim(), $options: "i" },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedOrderFilter
              ? debouncedOrderFilter.split(",").map((value) => ({
                  order_id: { $regex: value.trim(), $options: "i" },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedClassificationFilter
              ? debouncedClassificationFilter.split(",").map((value) => ({
                  "evaluation.metadata.classification": {
                    $regex: `^${value.trim()}`,
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: nameFilterOr,
        },
        {
          $or: [
            ...(debouncedConfidenceFilter
              ? debouncedConfidenceFilter.split(",").map((value) => ({
                  "evaluation.metadata.confidence": {
                    $gte: parseFloat(value.trim()) || 0,
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedCaptureCountFilter
              ? debouncedCaptureCountFilter.split(",").map((value) => {
                  const count = Number.parseInt(value.trim(), 10);

                  if (Number.isNaN(count)) {
                    return {};
                  }

                  if (count === 0) {
                    return {
                      $or: [
                        { capture_count: { $eq: 0 } },
                        { capture_count: null },
                        { capture_count: { $exists: false } },
                      ],
                    };
                  }

                  return { capture_count: { $eq: count } };
                })
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedComponentCountFilter
              ? debouncedComponentCountFilter.split(",").map((value) => ({
                  "evaluation.metadata.component_count": {
                    $eq: parseInt(value.trim()) || 0,
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedNeuralClassificationFilter
              ? debouncedNeuralClassificationFilter.split(",").map((value) => ({
                  "evaluation.metadata.neural_classification": {
                    $regex: value.trim(),
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedTraditionalClassificationFilter
              ? debouncedTraditionalClassificationFilter
                  .split(",")
                  .map((value) => ({
                    "evaluation.metadata.traditional_classification": {
                      $regex: value.trim(),
                      $options: "i",
                    },
                  }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedDonenessFilter
              ? debouncedDonenessFilter.split(",").map((value) => {
                  const lowerValue = value.trim().toLowerCase();
                  if (lowerValue.includes("burnt")) {
                    return {
                      "evaluation.metadata.doneness_evaluation.burnt.status":
                        true,
                    };
                  } else if (lowerValue.includes("raw")) {
                    return {
                      "evaluation.metadata.doneness_evaluation.raw.status":
                        true,
                    };
                  }
                  return {};
                })
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedOverallEvaluationFilter
              ? debouncedOverallEvaluationFilter.split(",").map((value) => ({
                  "evaluation.metadata.overall_evaluation.status": {
                    $regex: `^${value.trim()}$`,
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedComponentsEvaluationFilter
              ? debouncedComponentsEvaluationFilter.split(",").map((value) => {
                  const lowerValue = value.trim().toLowerCase();
                  if (lowerValue.includes("base")) {
                    return {
                      "evaluation.metadata.components_evaluation.base_components.status":
                        { $regex: value.trim(), $options: "i" },
                    };
                  } else if (lowerValue.includes("extra")) {
                    return {
                      "evaluation.metadata.components_evaluation.extras.status":
                        { $regex: value.trim(), $options: "i" },
                    };
                  } else if (lowerValue.includes("removal")) {
                    return {
                      "evaluation.metadata.components_evaluation.removals.status":
                        { $regex: value.trim(), $options: "i" },
                    };
                  }
                  return {
                    $or: [
                      {
                        "evaluation.metadata.components_evaluation.base_components.status":
                          { $regex: value.trim(), $options: "i" },
                      },
                      {
                        "evaluation.metadata.components_evaluation.extras.status":
                          { $regex: value.trim(), $options: "i" },
                      },
                      {
                        "evaluation.metadata.components_evaluation.removals.status":
                          { $regex: value.trim(), $options: "i" },
                      },
                    ],
                  };
                })
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedPizzaSaverEvaluationFilter
              ? debouncedPizzaSaverEvaluationFilter.split(",").map((value) => ({
                  "evaluation.metadata.pizza_saver_evaluation.status": {
                    $regex: value.trim(),
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedPizzaTypeFilter.length > 0
              ? debouncedPizzaTypeFilter.map((pizzaTypeId) => {
                  // Match pizza type based on name pattern like "Pizza 123. Name"
                  return {
                    name: {
                      $regex: `^[^0-9]*\\s?${pizzaTypeId}\\.\\s`,
                      $options: "i",
                    },
                  };
                })
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedOverallEvaluationStatusFilter.length > 0
              ? debouncedOverallEvaluationStatusFilter.map((status) => ({
                  "evaluation.metadata.overall_evaluation.status": {
                    $regex: `^${status}$`,
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedBaseComponentsStatusFilter.length > 0
              ? debouncedBaseComponentsStatusFilter.map((status) => ({
                  "evaluation.metadata.components_evaluation.base_components.status":
                    {
                      $regex: `^${status}$`,
                      $options: "i",
                    },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedExtrasStatusFilter.length > 0
              ? debouncedExtrasStatusFilter.map((status) => ({
                  "evaluation.metadata.components_evaluation.extras.status": {
                    $regex: `^${status}$`,
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedRemovalsStatusFilter.length > 0
              ? debouncedRemovalsStatusFilter.map((status) => ({
                  "evaluation.metadata.components_evaluation.removals.status": {
                    $regex: `^${status}$`,
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedPizzaSaverStatusFilter.length > 0
              ? debouncedPizzaSaverStatusFilter.map((status) => ({
                  "evaluation.metadata.pizza_saver_evaluation.status": {
                    $regex: `^${status}$`,
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedPhotoQualityStatusFilter.length > 0
              ? debouncedPhotoQualityStatusFilter.map((status) => ({
                  "evaluation.metadata.photo_quality_evaluation.status": {
                    $regex: `^${status}$`,
                    $options: "i",
                  },
                }))
              : [{}]),
          ],
        },
        {
          $or: [
            ...(debouncedPhotoQualityCheckFilter.length > 0
              ? debouncedPhotoQualityCheckFilter.map(
                  (check) => PHOTO_QUALITY_CHECK_CONDITIONS[check] ?? {},
                )
              : [{}]),
          ],
        },
        {
          $or:
            componentErrorConditions.length > 0
              ? componentErrorConditions
              : [{}],
        },
        ...(debouncedHasExtrasFilter
          ? [
              {
                "evaluation.metadata.components_evaluation.extras.expected": {
                  $exists: true,
                  $ne: [],
                  $not: { $size: 0 },
                },
              },
            ]
          : []),
        ...(debouncedHasRemovalsFilter
          ? [
              {
                "evaluation.metadata.components_evaluation.removals.not_expected":
                  {
                    $exists: true,
                    $ne: [],
                    $not: { $size: 0 },
                  },
              },
            ]
          : []),
      ],
      ...(captureAtFilter ? { capture_at: captureAtFilter } : {}),
    };
  }, [
    branchFilter,
    debouncedIdFilter,
    debouncedOrderFilter,
    debouncedClassificationFilter,
    debouncedNameFilter,
    debouncedCaptureAtFilterStart,
    debouncedCaptureAtFilterEnd,
    debouncedConfidenceFilter,
    debouncedCaptureCountFilter,
    debouncedComponentCountFilter,
    debouncedNeuralClassificationFilter,
    debouncedTraditionalClassificationFilter,
    debouncedDonenessFilter,
    debouncedOverallEvaluationFilter,
    debouncedComponentsEvaluationFilter,
    debouncedPizzaSaverEvaluationFilter,
    debouncedPizzaTypeFilter,
    debouncedOverallEvaluationStatusFilter,
    debouncedBaseComponentsStatusFilter,
    debouncedExtrasStatusFilter,
    debouncedRemovalsStatusFilter,
    debouncedPizzaSaverStatusFilter,
    debouncedHasExtrasFilter,
    debouncedHasRemovalsFilter,
    debouncedPhotoQualityStatusFilter,
    debouncedPhotoQualityCheckFilter,
    debouncedComponentErrorFilter,
  ]);

  const {
    data,
    isFetching: pizzasFetching,
    dataUpdatedAt: pizzasUpdatedAt,
    refetch: refetchPizzas,
  } = usePostGetAllPizzas<PizzasDTO>(
    {
      skip: page * perPage,
      limit: perPage,
      current_cursor: null,
      sort_by: sortBy,
      sort_order: sortOrder === "asc" ? 1 : -1,
      filters,
    },
    {
      query: {
        enabled: hasPermission("pizzas") || hasPermission("pizzas.branch"),
        // staleTime: 10000,
        // placeholderData: keepPreviousData,
      },
    },
  );

  // NOK share of the filtered list: one count with the verdict added to the same filters
  const { data: nokData } = useQuery({
    queryKey: ["pizzas-nok-count", filters],
    queryFn: () =>
      postPizzasCount({
        filters: {
          ...filters,
          $and: [
            ...filters.$and,
            { "evaluation.metadata.overall_evaluation.status": "NOK" },
          ],
        },
      }),
    enabled: hasPermission("pizzas") || hasPermission("pizzas.branch"),
    staleTime: 30 * 1000,
  });

  const locatePizza = useCallback(
    async (pizza: object, { fresh = false }: { fresh?: boolean } = {}) => {
      const value = getValueByPath(pizza, sortBy);
      if (!["string", "number", "boolean"].includes(typeof value)) {
        return null;
      }
      // pizzas sorted before this one; the clause goes into $and so that it
      // does not replace a capture_at range of the date filter
      const before: Record<string, unknown> = {
        [sortOrder === "asc" ? "$lt" : "$gt"]: value,
      };
      if (fresh) {
        // The backend caches counts per filter for 30 s and serves an expired
        // entry once more, however old: a position from before the newest
        // captures. A value no pizza has makes the filter unique (and stays on
        // the capture_at index).
        before.$ne = `~locate ${Date.now()} ${Math.random()}`;
      }
      const { count } = await postPizzasCount({
        filters: {
          ...filters,
          $and: [...filters.$and, { [sortBy]: before }],
        },
      });
      return count;
    },
    [filters, sortBy, sortOrder],
  );

  return (
    <PizzaContext.Provider
      value={useMemo(
        () => ({
          pizzas: data?.pizzas,
          pizzasFetching,
          pizzasUpdatedAt,
          refetchPizzas,
          page,
          setPage,
          perPage,
          setPerPage,
          totalPizzas: data?.total_pizzas,
          nokTotal: nokData?.count,
          totalPages: data?.total_pages,
          idFilter,
          setIdFilter,
          orderFilter,
          setOrderFilter,
          sortBy,
          setSortBy,
          sortOrder,
          setSortOrder,
          captureAtFilterStart,
          setCaptureAtFilterStart,
          captureAtFilterEnd,
          setCaptureAtFilterEnd,
          classificationFilter,
          setClassificationFilter,
          nameFilter,
          setNameFilter,
          confidenceFilter,
          setConfidenceFilter,
          captureCountFilter,
          setCaptureCountFilter,
          componentCountFilter,
          setComponentCountFilter,
          neuralClassificationFilter,
          setNeuralClassificationFilter,
          traditionalClassificationFilter,
          setTraditionalClassificationFilter,
          donenessFilter,
          setDonenessFilter,
          overallEvaluationFilter,
          setOverallEvaluationFilter,
          componentsEvaluationFilter,
          setComponentsEvaluationFilter,
          pizzaSaverEvaluationFilter,
          setPizzaSaverEvaluationFilter,
          pizzaTypeFilter,
          setPizzaTypeFilter,
          overallEvaluationStatusFilter,
          setOverallEvaluationStatusFilter,
          baseComponentsStatusFilter,
          setBaseComponentsStatusFilter,
          extrasStatusFilter,
          setExtrasStatusFilter,
          removalsStatusFilter,
          setRemovalsStatusFilter,
          pizzaSaverStatusFilter,
          setPizzaSaverStatusFilter,
          hasExtrasFilter,
          setHasExtrasFilter,
          hasRemovalsFilter,
          setHasRemovalsFilter,
          photoQualityStatusFilter,
          setPhotoQualityStatusFilter,
          photoQualityCheckFilter,
          setPhotoQualityCheckFilter,
          componentErrorFilter,
          setComponentErrorFilter,
          resetFilters,
          locatePizza,
        }),
        [
          data,
          pizzasFetching,
          pizzasUpdatedAt,
          refetchPizzas,
          nokData,
          page,
          setPage,
          perPage,
          setPerPage,
          idFilter,
          setIdFilter,
          orderFilter,
          setOrderFilter,
          captureAtFilterStart,
          setCaptureAtFilterStart,
          captureAtFilterEnd,
          setCaptureAtFilterEnd,
          sortBy,
          setSortBy,
          sortOrder,
          setSortOrder,
          classificationFilter,
          setClassificationFilter,
          nameFilter,
          setNameFilter,
          confidenceFilter,
          setConfidenceFilter,
          captureCountFilter,
          setCaptureCountFilter,
          componentCountFilter,
          setComponentCountFilter,
          neuralClassificationFilter,
          setNeuralClassificationFilter,
          traditionalClassificationFilter,
          setTraditionalClassificationFilter,
          donenessFilter,
          setDonenessFilter,
          overallEvaluationFilter,
          setOverallEvaluationFilter,
          componentsEvaluationFilter,
          setComponentsEvaluationFilter,
          pizzaSaverEvaluationFilter,
          setPizzaSaverEvaluationFilter,
          pizzaTypeFilter,
          setPizzaTypeFilter,
          overallEvaluationStatusFilter,
          setOverallEvaluationStatusFilter,
          baseComponentsStatusFilter,
          setBaseComponentsStatusFilter,
          extrasStatusFilter,
          setExtrasStatusFilter,
          removalsStatusFilter,
          setRemovalsStatusFilter,
          pizzaSaverStatusFilter,
          setPizzaSaverStatusFilter,
          hasExtrasFilter,
          setHasExtrasFilter,
          hasRemovalsFilter,
          setHasRemovalsFilter,
          photoQualityStatusFilter,
          setPhotoQualityStatusFilter,
          photoQualityCheckFilter,
          setPhotoQualityCheckFilter,
          componentErrorFilter,
          setComponentErrorFilter,
          resetFilters,
          locatePizza,
        ],
      )}
    >
      {children}
    </PizzaContext.Provider>
  );
};

export const usePizzaContext = () => {
  const context = useContext(PizzaContext);
  if (!context) {
    throw new Error("usePizzaContext must be used within an PizzaProvider");
  }
  return context;
};
